import axios from 'axios';
const API = import.meta.env.VITE_API_URL || 'http://localhost:3001';
export interface RecentDemo {
  id: string; fileName: string; mapName: string; rawBytes: string; storedBytes: string;
  replayBytes: string; strategyBytes: string; status: string; strategyStatus: string; replayReady: boolean; error: string | null;
  mapNumber?: number | null; team1Score?: number | null; team2Score?: number | null;
  partCount?: number;
  mergeError?: string | null;
  sourceParts?: { id: string; fileName: string; partNumber: number | null; replayReady: boolean; error?: string | null }[];
  replayCoverage?: { complete: boolean; roundCount: number; expectedRounds: number | null; missingRounds: number[]; missingParts: number[]; unavailableParts: string[] } | null;
}
export interface RecentMatch {
  id: string; matchUrl: string; matchDate: string; expiresAt: string; team1Name: string; team2Name: string;
  team1LogoUrl?: string | null; team2LogoUrl?: string | null;
  eventName: string; stage: string | null; format: string; status: string; error: string | null; demos: RecentDemo[];
}
export interface RecentPage { items: RecentMatch[]; page: number; total: number; hasMore: boolean }
export interface RecentStats { matches: number; demos: number; retentionDays: number; compression: string;
  bytes: Record<string, string>; states: {status: string; _count: number}[]; deleting: number }
export interface RecentCleanupItem { id: string; wholeMatch: boolean; demoIds: string[]; maps: string[]; team1Name: string; team2Name: string; eventName: string; matchDate: string }
export const recentPro = {
  async cleanupPreview() { return (await axios.get<{ items: RecentCleanupItem[] }>(`${API}/admin/pro-recent/cleanup-preview`)).data; },
  async cleanupUnavailable(items: RecentCleanupItem[]) { return (await axios.post<{ matchesRemoved: number; mapsRemoved: number }>(`${API}/admin/pro-recent/cleanup-unavailable`, { items: items.map(({ id, wholeMatch, demoIds }) => ({ id, wholeMatch, demoIds })) })).data; },
  async list(page = 1, admin = false, signal?: AbortSignal, date?: string) {
    return (await axios.get<RecentPage>(`${API}/${admin ? 'admin' : 'demos'}/pro-recent`, { params: { page, ...(date ? { date } : {}) }, signal })).data;
  },
  async saveCopy(id: string, destination: 'personal' | 'vault') {
    return (await axios.post(`${API}/demos/pro-recent/${id}/${destination}-copy`, undefined, { timeout: 180_000 })).data;
  },
  async stats(signal?: AbortSignal) { return (await axios.get<RecentStats>(`${API}/admin/pro-recent/stats`, {signal})).data; },
  async analyze(id?: string) { return (await axios.post(`${API}/admin/pro-recent/${id ? `${id}/` : ''}analyze`)).data; },
  async remove(id: string) { return (await axios.delete(`${API}/admin/pro-recent/${id}`)).data; },
  async import(body: object) { return (await axios.post(`${API}/admin/pro-recent/imports`, body)).data; },
  async retry(id: string, body: object) { return (await axios.post(`${API}/admin/pro-recent/${id}/retry`, body)).data; },
  async uploadArchive(id: string, file: File, progress: (percent: number) => void) {
    if (!/\.(rar|zip)$/i.test(file.name) || file.size > 5 * 1024 ** 3) throw new Error('Choose a RAR or ZIP archive up to 5 GiB.');
    const data = new FormData(); data.append('archive', file);
    const uploadApi = API.startsWith('https://stratsync.pro') ? 'https://upload.stratsync.pro' : API;
    return (await axios.post(`${uploadApi}/admin/pro-recent/${id}/archive`, data, {
      timeout: 2 * 3600_000,
      headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
      onUploadProgress: event => progress(Math.round(100 * event.loaded / (event.total || file.size))),
    })).data;
  },
};
export function recentBytes(value: string | number) {
  const n = Number(value);
  return n >= 1024 ** 3 ? `${(n / 1024 ** 3).toFixed(2)} GiB` : `${(n / 1024 ** 2).toFixed(1)} MiB`;
}
export function recentError(error: unknown) {
  return axios.isAxiosError(error) && typeof error.response?.data?.message === 'string'
    ? error.response.data.message : error instanceof Error && !axios.isAxiosError(error) ? error.message : 'Unable to load the recent library. Please retry.';
}
